/**
 * Money received past a full Mexican issuer, waiting for a decision (owner, 2026-10-21).
 *
 * Read and decided by a manager or a super_admin. Deciding moves no money: giving the money back
 * is the ordinary withdrawal flow, started by the client.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export type HoldState = 'waiting' | 'decision_due' | 'return_offered' | 'ended';
export type HoldDecision = 'offer_return' | 'keep_waiting';

export interface HoldEvent {
  kind: 'decision_due' | 'return_offered' | 'keep_waiting' | 'hold_ended';
  cycle: number;
  at: string;
  decided_by: string | null;
  reason: string | null;
}

export interface HeldDeposit {
  legal_entity_id: string;
  client_id: string;
  client_name: string;
  product_id: string;
  product_name: string;
  amount: string;
  currency_code: string;
  held_since: string;
  days_held: number;
  state: HoldState;
  cycle: number;
  next_decision_on: string | null;
  history: HoldEvent[];
}

export interface HoldDecisionResult {
  decision: HoldDecision;
  cycle: number;
  follow_up_created: boolean;
}

const BASE = '/api/v1/admin/concentration-accounts/held-deposits';

class HeldDepositsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async list(): Promise<HeldDeposit[]> {
    const res = await this.api.get<HeldDeposit[]>(BASE);
    return res.data;
  }

  async decide(
    hold: Pick<HeldDeposit, 'legal_entity_id' | 'client_id'>,
    decision: HoldDecision,
    reason: string | null,
  ): Promise<HoldDecisionResult> {
    const res = await this.api.post<HoldDecisionResult>(
      `${BASE}/${hold.legal_entity_id}/${hold.client_id}/decision`,
      { decision, reason },
    );
    return res.data;
  }
}

export default new HeldDepositsService();
